import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsPeople } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Card from '#globalComponents/Card/Card';
import EmptyState from '#globalComponents/EmptyState/EmptyState';
import { getErrorMessage } from '#lib/errors/errorMessages';
import { useRemoveMember, useChangeMemberRole } from '#orgFeature/models/org/mutations';

import MemberRow from './MemberRow';
import { TransferCreditsModal } from './TransferCreditsModal';

export const MemberList = ({ members, currentUserId, orgRole }) => {
	const [editingBudgetId, setEditingBudgetId] = useState(null);
	const [confirmRemoveId, setConfirmRemoveId] = useState(null);
	const [transferDataOnRemove, setTransferDataOnRemove] = useState(true);
	const [transferSenderId, setTransferSenderId] = useState(null);

	const removeMutation = useRemoveMember();
	const changeRoleMutation = useChangeMemberRole();

	const isOwner = orgRole === 'owner';
	const isAdminOrOwner = orgRole === 'owner' || orgRole === 'admin';

	const handleRemove = (userId) => {
		removeMutation.mutate(
			{ userId, transferData: transferDataOnRemove },
			{ onSuccess: () => setConfirmRemoveId(null) },
		);
	};

	const handleRoleChange = (userId, newRole) => {
		changeRoleMutation.mutate({ userId, role: newRole });
	};

	return (
		<Card className="overflow-hidden p-0">
			<div className="border-b border-bgBase-500 px-4 py-3">
				<h3 className="text-sm font-semibold text-text">Mitglieder</h3>
			</div>

			{changeRoleMutation.isError && (
				<div className="border-b border-bgBase-500 p-4">
					<Alert tone="warning">{getErrorMessage(changeRoleMutation.error)}</Alert>
				</div>
			)}

			<div className="divide-y divide-bgBase-500">
				{members.map((member) => (
					<MemberRow
						key={member.userId}
						member={member}
						isCurrentUser={member.userId === currentUserId}
						isOwner={isOwner}
						isAdminOrOwner={isAdminOrOwner}
						isEditingBudget={editingBudgetId === member.userId}
						isConfirmingRemove={confirmRemoveId === member.userId}
						transferDataOnRemove={transferDataOnRemove}
						onToggleBudget={(id) => setEditingBudgetId(editingBudgetId === id ? null : id)}
						onToggleRemove={(id) => setConfirmRemoveId(confirmRemoveId === id ? null : id)}
						onRemove={handleRemove}
						onRoleChange={handleRoleChange}
						onTransfer={setTransferSenderId}
						onTransferDataToggle={setTransferDataOnRemove}
						changeRolePending={changeRoleMutation.isPending}
						removeMutation={removeMutation}
					/>
				))}

				{members.length === 0 && <EmptyState icon={BsPeople} title="Noch keine Mitglieder vorhanden" />}
			</div>

			<TransferCreditsModal
				isOpen={transferSenderId !== null}
				onClose={() => setTransferSenderId(null)}
				members={members}
				senderId={transferSenderId}
			/>
		</Card>
	);
};

MemberList.propTypes = {
	members: PropTypes.arrayOf(
		PropTypes.shape({
			userId: PropTypes.string.isRequired,
			email: PropTypes.string,
			fullName: PropTypes.string,
			role: PropTypes.string,
			companyCredits: PropTypes.number,
			contactCredits: PropTypes.number,
			companyMonthlyBudget: PropTypes.number,
			contactMonthlyBudget: PropTypes.number,
		}),
	).isRequired,
	currentUserId: PropTypes.string.isRequired,
	orgRole: PropTypes.string.isRequired,
};
